import { Col } from 'antd';
import { INPUT_TYPE } from 'components/common/Input/types';
import DataCard from 'components/DataCard';
import { DEFAULT_FORMAT_DATE_US } from 'constants/days';
import { FormType } from 'constants/formTypes';
import { usStatesSelectOptions } from 'constants/usStates';
import useFormikRefHook from 'hooks/useFormikRefHook';
import { get } from 'lodash';
import moment from 'moment';
import React, { ReactElement, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CREATE_DOCUMENT, DELETE_DOCUMENT, UPDATE_DOCUMENT } from 'services/graphql/mutations/documents';
import { ICommissionDocumentDetails, IDocumentType } from 'types/user';
import { getFormattedDate, getUsStates } from 'utils';
import { formatMessage } from 'utils/IntlMessages';
import * as Yup from 'yup';

import NotaryFileUploadTemplate from '../NotaryFileUploadTemplate';
import ExtraField from '../NotaryFileUploadTemplate/ExtraField';
import RecordModal from '../NotaryFileUploadTemplate/RecordModal';
import { InputProps, ITabProps, NotaryDocumentType } from '../types';
import { DataCardActionType, ExtraFieldStatus, IDataCardAction } from './types';

const RecordFormSchema = {
  state: Yup.string().required('Required'),
  county: Yup.string().required('Required'),
  commissionPeriod: Yup.array().length(2, 'Need both start and end date').required('Required'),
  commissionNo: Yup.string().required('Required'),
  files: Yup.array().min(1, 'Need at least 1 document').required('Need at least 1 document'),
};

// NOTARY COMMISSION
const NotaryCommissions: React.FC<ITabProps> = ({ user, refetchUserData }) => {
  const intl = useIntl();
  const { id } = user;
  const documents = get(user, 'documents') || [];
  useFormikRefHook({
    name: 'notaryCommissions',
  });
  const formFields: InputProps[] = useMemo(
    () => [
      {
        label: 'County',
        name: 'county',
        sideFields: [
          {
            label: 'State',
            name: 'state',
            type: INPUT_TYPE.SELECT,
            inputOptions: usStatesSelectOptions,
          },
        ],
      },
      { label: 'Commission Number', name: 'commissionNo' },
      { label: 'Commission Period', name: 'commissionPeriod', type: INPUT_TYPE.RANGE_PICKER },
      {
        label: 'Certifcates',
        name: 'files',
        type: INPUT_TYPE.FILE,
        inputSettings: {
          accept: 'image/*, .pdf',
        },
      },
    ],
    [],
  );

  const detailsFields = ['commissionPeriod', 'county', 'state', 'commissionNo'];

  const [formType, setFormType] = useState<FormType | undefined>();
  const [chosenCommission, setChosenCommission] = useState<any>();
  const closeModal = () => {
    setFormType(undefined);
  };

  const showCommissionForm = (type: FormType) => {
    setFormType(type);
  };

  const generateCommissionCards = (acc: any, comm: IDocumentType<ICommissionDocumentDetails>): ReactElement => {
    const { type, details, id: commId, files } = comm;
    if (type !== NotaryDocumentType.COMMISSION) return acc;
    const { state, county, commissionNo, commissionPeriod } = details;
    const { originThumbnail, resizedThumbnail } = files.reduce(
      (thumbnailAcc, f) => {
        thumbnailAcc.originThumbnail.push(f.pipeFrom || f.thumbnailPipeFrom);
        thumbnailAcc.resizedThumbnail.push(f.thumbnailPipeFrom || f.pipeFrom);
        return thumbnailAcc;
      },
      { originThumbnail: [], resizedThumbnail: [] } as Record<string, string[]>,
    );
    const dayFrom = moment(getFormattedDate(get(commissionPeriod, '0', ''))[0]).format(DEFAULT_FORMAT_DATE_US);
    const dayTo = moment(getFormattedDate(get(commissionPeriod, '1', ''))[0]).format(DEFAULT_FORMAT_DATE_US);
    const isCommissionExpired = Date.now() - new Date(dayTo).getTime() > 0;
    const status = isCommissionExpired ? ExtraFieldStatus.DANGER : ExtraFieldStatus.DEFAULT;
    const extraFields = [
      <ExtraField labelId='userProfile.notaryCommissions.commissionNo' data={commissionNo || ''} />,
      <ExtraField labelId='userProfile.notaryCommissions.commissionStartDate' data={dayFrom} />,
      <ExtraField
        labelId='userProfile.notaryCommissions.commissionEndDate'
        status={status}
        data={dayTo}
        tooltip={status === ExtraFieldStatus.DANGER ? 'Expired' : ''}
      />,
    ];

    const filesValueForInput = files.map((f) => ({
      uid: f.id,
      status: 'done',
      name: f.name,
      url: f.pipeFrom,
    }));

    const action: IDataCardAction = {
      type: DataCardActionType.INTERACT,
      edit: () => {
        // todo: show edit comm form
        showCommissionForm(FormType.EDIT);
        setChosenCommission(() => ({
          ...details,
          id: commId,
          files: filesValueForInput,
        }));
      },
      delete: () => {
        // todo: show confirmation box
        showCommissionForm(FormType.DELETE);
        setChosenCommission(comm);
      },
    };
    acc.push(
      <Col key={commId} span={24} xl={12} style={{ minWidth: 400 }}>
        <DataCard
          style={{ minWidth: 400, maxWidth: '100%' }}
          mobileWidth='280px'
          mobileHeight='auto'
          responsive
          mobileThreshold={630}
          avatarSizesMobile={{ height: '120px' }}
          avatarSizes={{ width: '150px' }}
          avatar={resizedThumbnail}
          originAvatar={originThumbnail}
          main={state ? getUsStates(state) : ''}
          sub={county}
          extra={extraFields}
          action={action}
          height='200px'
        />
      </Col>,
    );
    return acc;
  };
  return (
    <div className='' style={{ padding: '25px 0' }}>
      <RecordModal
        refetchUserData={refetchUserData}
        formType={formType}
        record={chosenCommission}
        closeModal={closeModal}
        userId={id}
        formTitle={
          formType && formType !== FormType.DELETE
            ? formatMessage(intl, `userProfile.notaryCommissions.${formType}`)
            : ''
        }
        updateMutation={{ mutation: UPDATE_DOCUMENT, key: 'updateDocumentEntity' }}
        createMutation={{ mutation: CREATE_DOCUMENT, key: 'createDocumentEntity' }}
        deleteMutation={DELETE_DOCUMENT}
        formFields={formFields}
        detailsFields={detailsFields}
        documentType={NotaryDocumentType.COMMISSION}
        validationSchema={RecordFormSchema}
      />
      <NotaryFileUploadTemplate
        RecordCards={documents.reduce(generateCommissionCards, [] as any)}
        createNewButtonTitle={formatMessage(intl, 'userProfile.notaryCommissions')}
        onClickCreateNewButton={() => {
          showCommissionForm(FormType.ADD);
        }}
        noDataMessage='No Commissions - Add your Notary Commission'
      />
    </div>
  );
};

export default React.memo(NotaryCommissions);
